import { useState } from 'react';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import { useEffect } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { projectUserRole } from 'redux/projectSlice';
import {
  useGetPaginatedInspectionsMutation,
  useGetLocationsMutation,
  useGetCountsMutation,
  useGetReportMutation,
} from 'redux/services/inspection';
import { useGetChecklist } from 'hooks/useGetChecklist';
import { Loader } from 'components/common/Loader';
import { IInspection, IInspectionReport } from 'redux/conductInspectionSlice';
import { WorkFlowCard } from 'components/common/WorkFlowCard';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { useTranslation } from 'react-i18next';
import { WorkFlowHeader } from 'components/common/WorkFlowHeader';
import { Button, Pagination } from '@mui/material';
import {
  getAllSubTasks,
  getFilterValueByType,
  getSupportedFilterOptionForAPI,
} from 'pages/utils';
import { INSPECTION_WORK_FLOW_OPTIONS } from 'pages/constants';
import { RescheduleInspectionModal } from 'pages/ScheduledInspections/RescheduleInspectionModal';
import { DeleteInspectionModal } from 'pages/ScheduledInspections/DeleteInspectionModal';
import { StartInspectionModal } from 'pages/ScheduledInspections/StartInspectionModal';
import { ArrowBack } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { downloadInspectionReport } from 'pages/ReviewInspection/downloadUtils';
import './Inspection.scss';

const Inspection = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const [getAllInspections, { data: response, isLoading }] =
    useGetPaginatedInspectionsMutation();

  const [getReport] = useGetReportMutation();

  const [selectedInspection, setSelectedInspection] =
    useState<IInspection | null>(null);
  const [showStartInspection, setShowStartInspection] = useState(false);
  const [showCancelInspection, setShowCancelInspection] = useState(false);
  const [showRescheduleInspection, setShowRescheduleInspection] =
    useState(false);

  const [getLocations, { data: locations }] = useGetLocationsMutation();
  const [getCounts, { data: counts, isLoading: countsLoading }] =
    useGetCountsMutation();
  const [status, setStatus] = useState('');
  const [filterValue, setFilterValue] = useState<{
    id?: string;
    name: string;
  } | null>(null);
  const [filterByValue, setFilterByValue] = useState<string | null>(null);
  const [filterByValues, setFilterByValues] = useState<
    { id?: string; name: string }[]
  >([]);

  const { project } = useSetDropdownAndRole();
  const checklist = useGetChecklist();
  const navigate = useNavigate();

  useEffect(() => {
    if (project) {
      dispatch(projectUserRole(project.role));
    }
  }, [dispatch, project]);

  useEffect(() => {
    getAllInspections({
      id: projectId,
      page: 1,
    });
    getLocations({ id: projectId });
    getCounts({ id: projectId });
  }, [dispatch, getAllInspections, getLocations, getCounts, projectId]);

  const resetState = () => {
    setFilterByValues([]);
    setFilterByValue(null);
    setFilterValue(null);
  };

  const handlePaginationChange = (
    _: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    if (Number(response?.pagination?.page) !== value) {
      getAllInspections({
        id: projectId,
        page: value,
        status: status.toUpperCase(),
        filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
        filterValue: getFilterValueByType(filterValue, filterByValue),
      });
    }
  };

  const handleStatusChange = (changedStatus: string) => {
    setStatus(changedStatus.toUpperCase());
    getAllInspections({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status: changedStatus.toUpperCase(),
      filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
      filterValue: getFilterValueByType(filterValue, filterByValue),
    });
  };
  const handleClearStatus = () => {
    setStatus('');
    getAllInspections({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status: '',
      filterBy: getSupportedFilterOptionForAPI(filterByValue || ''),
      filterValue: getFilterValueByType(filterValue, filterByValue),
    });
  };
  const handleSelectOption = (option: string) => {
    setFilterByValue(option);
    if (filterValue) {
      setFilterValue(null);
      getAllInspections({
        id: projectId,
        page: Number(response?.pagination?.page ?? 1),
      });
    }
    if (option === 'User') {
      setFilterByValues(project?.team ?? []);
    } else if (option === 'Checklist') {
      setFilterByValues(checklist ?? []);
    } else if (option === 'Tags') {
      setFilterByValues(locations?.map((name: string) => ({ name })) ?? []);
    } else if (option === 'Sub Project') {
      setFilterByValues(getAllSubTasks(project?.tasks ?? []));
    } else {
      resetState();
    }
  };

  const handleSearchSelect = (option: { id?: string; name: string } | null) => {
    const filterValue = getFilterValueByType(option, filterByValue);

    setFilterValue(option);
    getAllInspections({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status,
      filterBy: filterValue
        ? getSupportedFilterOptionForAPI(filterByValue || '')
        : '',
      filterValue: filterValue || '',
    });
  };

  const handleCloseCancelInspection = () => {
    setShowCancelInspection(false);
    setSelectedInspection(null);
  };

  const handleCloseRescheduleInspection = () => {
    setShowRescheduleInspection(false);
    setSelectedInspection(null);
  };

  const handleCloseStartInspection = () => {
    setShowStartInspection(false);
    setSelectedInspection(null);
  };

  const handleSuccess = () => {
    getAllInspections({
      id: projectId,
      page: Number(response?.pagination?.page ?? 1),
      status,
      filterBy: filterValue
        ? getSupportedFilterOptionForAPI(filterByValue || '')
        : '',
      filterValue: filterValue || '',
    });
    getCounts({ id: projectId });
  };

  const getCardClickHandler = (item: IInspection) => () => {
    setSelectedInspection(item);
    setShowStartInspection(true);
  };

  const getContinueDraftHandler = (item: IInspection) => () => {
    navigate(`/project/${projectId}/conduct-inspection/${item._id}/1`);
  };

  const handleClearSearchOptions = () => {
    resetState();
  };

  const handleBack = () => navigate(`/project/${projectId}`);

  const getDownloadHandler = (inspectionId: string) => async () => {
    toast.success('Report will be downloaded shortly');
    const res: any = await getReport({
      id: inspectionId,
    });

    if (res.data as IInspectionReport) {
      downloadInspectionReport(res.data, project?.team ?? []);
    } else {
      toast.error('Report generation failed. Please try again later');
    }
  };

  return (
    <div className="inspection">
      <h4 className="page-title">{t('header_inspection')}</h4>
      {response?.data?.length || status || filterByValue ? (
        <WorkFlowHeader
          scheduled={counts?.SCHEDULED}
          open={counts?.OPEN}
          closed={counts?.CLOSE}
          draft={counts?.DRAFT}
          canceled={counts?.CANCELLED}
          isLoading={countsLoading}
          options={INSPECTION_WORK_FLOW_OPTIONS}
          selectedSearchOption={filterValue}
          selectedOption={filterByValue}
          searchOptions={filterByValues}
          status={status}
          onStatusClick={handleStatusChange}
          onOptionClick={handleSelectOption}
          onSearchSelect={handleSearchSelect}
          onClearStatus={handleClearStatus}
          onClearSearchOptions={handleClearSearchOptions}
        />
      ) : null}

      <div className="d-flex setup-container gap-3">
        {(response?.data as IInspection[])?.map((item: IInspection) => (
          <WorkFlowCard
            key={item._id}
            date={
              item.scheduledDate
                ? moment(item.scheduledDate).format('DD/MM/YYYY hh:mm A')
                : moment(item.updated_at || item.created_at).format(
                    'DD/MM/YYYY',
                  )
            }
            role={project?.role ?? ''}
            title={item.displayId ?? ''}
            doer={
              project?.team?.find((teamObj) => teamObj.userId === item.doer)
                ?.name ?? ''
            }
            inspector={
              project?.team?.find(
                (teamObj) => teamObj.userId === item.inspector,
              )?.name ?? ''
            }
            status={item.status as WorkFlowStatus}
            projects={Object.values({
              subProject: item.subProject?.name ?? '',
              subTask: item.subTask?.name ?? '',
              endTask: item.endTask?.name ?? '',
            }).filter((value) => value)}
            locations={item.locations}
            onCardClick={getCardClickHandler(item)}
            onCancelOptionClick={() => {
              setSelectedInspection(item);
              setShowCancelInspection(true);
            }}
            onRescheduleOptionClick={() => {
              setSelectedInspection(item);
              setShowRescheduleInspection(true);
            }}
            onDownload={getDownloadHandler(item._id)}
            onContinueDraft={getContinueDraftHandler(item)}
          />
        ))}
        {isLoading && <Loader />}
        {!isLoading && !response?.data?.length && !status && !filterByValue ? (
          <div className="d-flex justify-content-center w-100 flex-grow-1 align-items-center flex-column">
            <div className="no-inspection-placeholder" />
            <span className="h6 text-center">{t('no_inspection_text')}</span>
          </div>
        ) : (
          !isLoading &&
          !response?.data?.length &&
          (status || filterByValue) && (
            <span className="d-flex justify-content-center align-items-center w-100 h5">
              {t('no_matching_results')}
            </span>
          )
        )}
      </div>

      <div className="d-flex justify-content-between my-1">
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          {t('btn_back')}
        </Button>
        {response?.pagination?.totalPage > 1 && (
          <Pagination
            className="align-self-end"
            count={response?.pagination?.totalPage ?? 0}
            page={Number(response?.pagination?.page ?? 1)}
            color="primary"
            shape="rounded"
            onChange={handlePaginationChange}
          />
        )}
      </div>

      {!!selectedInspection && showStartInspection && (
        <StartInspectionModal
          inspection={selectedInspection}
          open={showStartInspection}
          onClose={handleCloseStartInspection}
        />
      )}
      <RescheduleInspectionModal
        inspection={selectedInspection}
        open={showRescheduleInspection}
        onClose={handleCloseRescheduleInspection}
        onSuccess={handleSuccess}
      />
      <DeleteInspectionModal
        inspectionId={selectedInspection?._id ?? ''}
        open={showCancelInspection}
        onClose={handleCloseCancelInspection}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default Inspection;
